import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";

import { defineMessages, FormattedMessage } from "react-intl";
import { useLocaleProvider } from "../../components/locale-provider";

import { ClassicMenu } from "./menu";
import { InternalMenu } from "./internal-menu";
import { useUserAuthInfo } from "../../lib/auth";
import { PropertySelector } from "./property-selector";
import ArrowLeft from "../../modules/witadmin/icons/arrowLeft";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";
import { useWhiteLabel } from "../../components/white-label-provider";
import { LOGO_CONFIGURATION } from "../../modules/witadmin/Common/constants";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import { Freshdesk } from "./freshdesk";

import { SVGMenu } from "./assets";
import { useUserProfile } from "../../components/user-profile-provider";

const Content = styled.div`
  flex-grow: 1;
`;

const BackToMenuContent = styled.div`
  display: flex;
  align-content: center;
`;

const BackToMenuButton = styled.button`
  background-color: #f6f7fa;
  color: #656565;
  padding: 5px 20px 5px 20px;
  border: none;
  margin-top: 20px;
  margin-bottom: 10px;
  border-radius: 2px;
  font-size: 14px;
  outline: none;

  &:focus {
    outline: 0;
  }

  svg {
    fill: #ababac;
    width: 22px;
    margin-right: 5px;
  }
`;

const ZendeskStyle = createGlobalStyle`
  #webWidget {
    bottom: inherit;
    right: 4px;
    top: 30px;
  }
`;

const messages = defineMessages({
  english: {
    id: "header.english",
    defaultMessage: "english",
  },
  spanish: {
    id: "header.spanish",
    defaultMessage: "español",
  },
  help: {
    id: "header.help",
    defaultMessage: "help",
  },
  support: {
    id: "header.support",
    defaultMessage: "support",
  },
  logout: {
    id: "header.logout",
    defaultMessage: "exit",
  },
  backToMenu: {
    id: "header.backToMenu",
    defaultMessage: "Back to main menu",
  },
});

interface ClassicLayoutProps {
  currentPropertyTicker?: string;
}

export function ClassicLayout({
  currentPropertyTicker,
  children,
}: PropsWithChildren<ClassicLayoutProps>) {
  const propertyTickersList = useUserPropertyTickers();
  const userProfile = useUserProfile();

  const propertyConfig = usePropertyConfiguration();
  const lastPropertyTickerKey = "lastPropertyTicker";
  const lastPropertyTicker = window.sessionStorage.getItem(lastPropertyTickerKey);
  const [userPropertyTickerRandom, setUserPropertyTickerRandom] = useState<string>("");

  const whiteLabel = useWhiteLabel();

  useEffect(() => {
    const favicon = whiteLabel?.logoConfigurations.find(
      logo => logo.logoType === LOGO_CONFIGURATION.FAVICON
    );
    if (favicon) {
      const faviconElement = document.getElementById("favicon") as HTMLLinkElement;
      if (favicon) {
        faviconElement.href = favicon.multimedia.url;
      }
    }
  }, [whiteLabel]);

  const showBackToMenu = useMemo(
    () =>
      !!(
        (document.referrer &&
          document.referrer !== window.location.href &&
          !document.referrer.includes("crm")) ||
        lastPropertyTicker ||
        userPropertyTickerRandom
      ),
    [lastPropertyTicker, userPropertyTickerRandom]
  );

  useEffect(() => {
    if (
      currentPropertyTicker &&
      lastPropertyTicker !== currentPropertyTicker &&
      !currentPropertyTicker?.includes("crm")
    ) {
      window.sessionStorage.setItem(lastPropertyTickerKey, currentPropertyTicker);
    } else if (userPropertyTickerRandom && !lastPropertyTicker) {
      window.sessionStorage.setItem(lastPropertyTickerKey, userPropertyTickerRandom);
    }
  }, [currentPropertyTicker, lastPropertyTicker, lastPropertyTickerKey, userPropertyTickerRandom]);

  useEffect(() => {
    async function loadUserPropertyTickers() {
      const randomTickerIndex = Math.floor(
        Math.random() * (propertyTickersList ? propertyTickersList.length : 1) - 1
      );
      setUserPropertyTickerRandom(propertyTickersList[randomTickerIndex]);
    }

    loadUserPropertyTickers().catch(err =>
      console.error("Error loading user propertyTickers", err)
    );
  }, [propertyTickersList]);

  const localeProvider = useLocaleProvider();

  useEffect(() => {
    if (window.zEmbed) {
      window.zEmbed(() => {
        window.zEmbed?.setLocale(localeProvider.currentLocale);
      });
    }
  }, [localeProvider.currentLocale]);

  const userAuthInfo = useUserAuthInfo();

  const goBackToMenu = useCallback(() => {
    if (showBackToMenu) {
      let url = "";
      // It's necessary to check if there is a lastPropertyTicker saved and if the document referrer doesnt exist.
      // If document referrer exists and lastPropertyTicker too, I check if it's from the same referrer
      const referrerIncludesPropertyTicker =
        lastPropertyTicker && document.referrer?.includes(lastPropertyTicker);
      const noReferrer = !Boolean(document.referrer);
      const referrerIsCurrentPage = document.referrer === window.location.href;
      if (
        userPropertyTickerRandom ||
        (lastPropertyTicker &&
          (referrerIncludesPropertyTicker || !noReferrer || referrerIsCurrentPage))
      ) {
        url = `${window.location.origin}/v6/ng/${
          lastPropertyTicker || userPropertyTickerRandom
        }/reservations`;
      } else if (
        document.referrer &&
        document.referrer !== window.location.href &&
        !document.referrer.includes("crm")
      ) {
        url = document.referrer;
      } else if (lastPropertyTicker) {
        url = `${window.location.origin}/v6/ng/${
          lastPropertyTicker || userPropertyTickerRandom
        }/reservations`;
      }
      if (url) {
        window.location.replace(url);
      }
    }
  }, [lastPropertyTicker, showBackToMenu, userPropertyTickerRandom]);

  return (
    <>
      <ZendeskStyle />
      <Freshdesk propertyTicker={currentPropertyTicker} userProfile={userProfile} />
      <div className="h-full flex flex-col">
        <div className="h-24 bg-[#EFF1F9] p-4 flex justify-content-between align-items-center">
          <div className="flex">
            <div className="border-r border-[#2E3862] mr-[25px]">
              <div className="w-1/12 justify-center mr-[25px]">{SVGMenu["LOGO"]}</div>
            </div>
            <div className="w-7/12 max-h-full flex flex-col justify-center">
              {currentPropertyTicker && (
                <ClassicMenu
                  propertyTicker={currentPropertyTicker}
                  useLegacyExtras={!propertyConfig?.["showServicesManager"]}
                  userProfile={userProfile}
                />
              )}
              {(userAuthInfo?.isWitAdmin ||
                userAuthInfo?.isCRMReadOnly ||
                userAuthInfo?.isC2tAdmin) && (
                <InternalMenu propertyTicker={currentPropertyTicker} />
              )}
            </div>
          </div>
          <div className="w-4/12">
            {currentPropertyTicker ? (
              <PropertySelector
                isWitAdmin={userAuthInfo?.isWitAdmin}
                userProfile={userProfile?.email}
              />
            ) : (
              showBackToMenu && (
                <div className="flex justify-end items-center text-[#2E3862]">
                  <BackToMenuButton onClick={goBackToMenu}>
                    <BackToMenuContent>
                      <ArrowLeft />
                      <FormattedMessage {...messages.backToMenu} />
                    </BackToMenuContent>
                  </BackToMenuButton>
                </div>
              )
            )}
          </div>
        </div>

        <Content>{children}</Content>
      </div>
    </>
  );
}
